/**
 * Classify a failed payer roster save (GDN-3386).
 *
 * Providers autosave the roster, so the banner is the only thing that tells
 * them what went wrong. An earlier version reported every failure as
 * "check your connection", which is actively misleading for a 403 or a
 * conflict: the reader retries a working connection forever. Each branch here
 * names a cause the message can actually stand behind, and the fallback claims
 * no cause at all.
 *
 * Mirrors the shape of `formatAdminSaveErrorMessage` in `adminSaveErrors.ts`,
 * which classifies the admin batch save by which kinds failed.
 */

/**
 * Why a roster save failed, in the terms the retry path cares about.
 * `conflict` and `stale_catalog` both mean the client's copy is out of date.
 */
export type PayerRosterSaveFailureKind =
  | 'permission'
  | 'conflict'
  | 'stale_catalog'
  | 'offline'
  | 'unknown';

const PERMISSION_MESSAGE =
  'You do not have permission to change this payer list. Ask a practice administrator.';
const CONFLICT_MESSAGE =
  'Someone else changed this payer list. Refresh the page and make your change again.';
const STALE_CATALOG_MESSAGE =
  'This intake was updated while you were editing. Refresh the page and try again.';
const OFFLINE_MESSAGE = 'We could not save your payer list. Check your connection and try again.';
const UNKNOWN_MESSAGE = 'We could not save your payer list. Try again.';

/** RTK Query reports transport failures as a string status rather than a code. */
const TRANSPORT_STATUSES = new Set(['FETCH_ERROR', 'TIMEOUT_ERROR']);

function errorStatus(error: unknown): number | string | undefined {
  if (typeof error !== 'object' || error === null || !('status' in error)) return undefined;
  const { status } = error;
  return typeof status === 'number' || typeof status === 'string' ? status : undefined;
}

export function classifyPayerRosterSaveError(error: unknown): PayerRosterSaveFailureKind {
  const status = errorStatus(error);
  if (typeof status === 'string') {
    return TRANSPORT_STATUSES.has(status) ? 'offline' : 'unknown';
  }
  if (status === 403) return 'permission';
  // The roster row is shared by the whole practice and a write replaces it, so
  // a concurrent edit is a real outcome, not an edge case.
  if (status !== undefined && status >= 409 && status <= 412) return 'conflict';
  if (status === 422) return 'stale_catalog';
  return 'unknown';
}

const MESSAGE_BY_KIND: Record<PayerRosterSaveFailureKind, string> = {
  conflict: CONFLICT_MESSAGE,
  offline: OFFLINE_MESSAGE,
  permission: PERMISSION_MESSAGE,
  stale_catalog: STALE_CATALOG_MESSAGE,
  unknown: UNKNOWN_MESSAGE,
};

export function payerRosterSaveErrorMessage(kind: PayerRosterSaveFailureKind): string {
  return MESSAGE_BY_KIND[kind];
}

export function formatPayerRosterSaveErrorMessage(error: unknown): string {
  return payerRosterSaveErrorMessage(classifyPayerRosterSaveError(error));
}

/**
 * Whether a retry must refetch the roster before resubmitting.
 *
 * `conflict` and `stale_catalog` both mean the server has moved past the copy
 * the client is holding, so replaying the same draft and catalog version just
 * fails again — or overwrites whoever changed it. Every other kind says
 * nothing about freshness, and refetching there would throw the provider's
 * unsaved edit away for no reason.
 */
export function payerRosterFailureNeedsRebase(kind: PayerRosterSaveFailureKind): boolean {
  return kind === 'conflict' || kind === 'stale_catalog';
}
